import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Typography, List, ListItem, ListItemText, Box, Button, TextField, IconButton, MenuItem, Stack, Divider, Switch, Chip, InputAdornment } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dhsComponentsService, { DHSComponent } from '../../services/dhsComponentsService';
import { usePageTitle } from '../../hooks/usePageTitle';

const DHSComponents: React.FC = () => {
  usePageTitle('DHS Components');
  
  const navigate = useNavigate();
  const [items, setItems] = useState<DHSComponent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<Partial<DHSComponent>>({});
  const [q, setQ] = useState<string>('');
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const load = async (query?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await dhsComponentsService.list(query);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load components');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (editingId && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [editingId]);

  const startEdit = (it: DHSComponent) => {
    setEditingId(it._id);
    setForm({ ...it });
  };

  const startCreate = () => {
    setEditingId('new');
    setForm({ name: '', acronym: '', category: 'Component', description: '', order: (items?.length || 0) + 1, isActive: true } as Partial<DHSComponent>);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({});
  };

  const save = async () => {
    try {
      if (editingId === 'new') {
        const created = await dhsComponentsService.create({
          name: form.name || '',
          acronym: form.acronym || undefined,
          category: (form.category as DHSComponent['category']) || 'Component',
          description: form.description || undefined,
          order: typeof form.order === 'number' ? form.order : undefined,
          isActive: typeof form.isActive === 'boolean' ? form.isActive : undefined,
        });
        setItems((prev) => [created, ...prev]);
      } else if (editingId) {
        const updated = await dhsComponentsService.update(editingId, {
          name: form.name,
          acronym: form.acronym,
          category: form.category as DHSComponent['category'],
          description: form.description,
          order: form.order,
          isActive: form.isActive,
        });
        setItems((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      }
      cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await dhsComponentsService.remove(id);
      setItems((prev) => prev.filter((p) => p._id !== id));
      if (editingId === id) cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Delete failed');
    }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [] as DHSComponent[];
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((i) =>
      [i.name, i.acronym, i.category, i.description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [items, q]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          onClick={() => navigate('/dhs')}
          startIcon={<ArrowBackIcon />}
          aria-label="Back to DHS Study"
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search components"
          aria-label="Search components"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            )
          }}
        />
        <Button
          onClick={startCreate}
          startIcon={<AddIcon />}
          aria-label="Add component"
          sx={{ ml: 2 }}
        >
          Add
        </Button>
      </Box>

      <Typography variant="h4" component="h1" sx={{ color: '#003366', fontWeight: 700, mb: 2 }}>
        DHS Components
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Major operational components and key headquarters offices relevant to governance, privacy, security, and mission support.
      </Typography>

      {error && (
        <Typography role="alert" aria-live="assertive" sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>
      )}

      <List aria-label="DHS components list">
        {editingId === 'new' && (
          <ListItem divider sx={{ bgcolor: 'background.default' }}>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  inputRef={firstFieldRef}
                  label="Name"
                  fullWidth
                  value={form.name || ''}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
                <TextField
                  label="Acronym"
                  sx={{ width: 160 }}
                  value={form.acronym || ''}
                  onChange={(e) => setForm((f) => ({ ...f, acronym: e.target.value }))}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  label="Category"
                  sx={{ width: 220 }}
                  value={form.category || 'Component'}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as DHSComponent['category'] }))}
                >
                  <MenuItem value="Component">Component</MenuItem>
                  <MenuItem value="Directorate">Directorate</MenuItem>
                  <MenuItem value="Office">Office</MenuItem>
                </TextField>
                <TextField
                  label="Order"
                  type="number"
                  sx={{ width: 120 }}
                  value={form.order ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))}
                />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
                  <Typography component="span">Active</Typography>
                  <Switch
                    checked={!!form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    inputProps={{ 'aria-label': 'Active' }}
                  />
                </Stack>
              </Stack>
              <TextField
                label="Description"
                multiline
                minRows={2}
                value={form.description || ''}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              <Stack direction="row" spacing={1}>
                <Button onClick={save} startIcon={<SaveIcon />} aria-label="Save new component" variant="contained">Save</Button>
                <Button onClick={cancelEdit} startIcon={<CloseIcon />} aria-label="Cancel" color="inherit">Cancel</Button>
              </Stack>
            </Stack>
          </ListItem>
        )}

        {(filtered || []).map((it) => (
          <ListItem key={it._id} divider>
            {editingId === it._id ? (
              <Stack spacing={1} sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    inputRef={firstFieldRef}
                    label="Name"
                    fullWidth
                    value={form.name || ''}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                  <TextField
                    label="Acronym"
                    sx={{ width: 160 }}
                    value={form.acronym || ''}
                    onChange={(e) => setForm((f) => ({ ...f, acronym: e.target.value }))}
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    select
                    label="Category"
                    sx={{ width: 220 }}
                    value={(form.category as DHSComponent['category']) || 'Component'}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as DHSComponent['category'] }))}
                  >
                    <MenuItem value="Component">Component</MenuItem>
                    <MenuItem value="Directorate">Directorate</MenuItem>
                    <MenuItem value="Office">Office</MenuItem>
                  </TextField>
                  <TextField
                    label="Order"
                    type="number"
                    sx={{ width: 120 }}
                    value={form.order ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))}
                  />
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
                    <Typography component="span">Active</Typography>
                    <Switch
                      checked={!!form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      inputProps={{ 'aria-label': 'Active' }}
                    />
                  </Stack>
                </Stack>
                <TextField
                  label="Description"
                  multiline
                  minRows={2}
                  value={form.description || ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
                <Stack direction="row" spacing={1}>
                  <Button onClick={save} startIcon={<SaveIcon />} aria-label={`Save ${it.name}`} variant="contained">Save</Button>
                  <Button onClick={cancelEdit} startIcon={<CloseIcon />} aria-label="Cancel" color="inherit">Cancel</Button>
                  <Button onClick={() => remove(it._id)} startIcon={<DeleteIcon />} aria-label={`Delete ${it.name}`} color="error">Delete</Button>
                </Stack>
              </Stack>
            ) : (
              <Box
                role="button"
                tabIndex={0}
                aria-label={`Edit ${it.name}`}
                onClick={() => startEdit(it)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(it); } }}
                style={{ width: '100%', outline: 'none' }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ListItemText primaryTypographyProps={{ sx: { fontWeight: 700 } }} primary={it.name} />
                    {it.acronym && <Chip size="small" label={it.acronym} />}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" color="primary" label={it.category} />
                    {typeof it.order === 'number' && <Chip size="small" label={`Order ${it.order}`} />}
                    <Chip size="small" label={it.isActive ? 'Active' : 'Inactive'} color={it.isActive ? 'success' : 'default'} />
                  </Stack>
                  {it.description && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{it.description}</Typography>
                  )}
                </Stack>
              </Box>
            )}
          </ListItem>
        ))}
        {loading && (
          <ListItem>
            <ListItemText primary="Loading..." />
          </ListItem>
        )}
        {!loading && filtered.length === 0 && (
          <ListItem>
            <ListItemText primary="No components found" />
          </ListItem>
        )}
      </List>
    </Container>
  );
};

export default DHSComponents;
